import {
  keralitProfileSystems,
  normalizeKeralitProfileSystem,
  plastisolProfileSystems,
  normalizePlastisolProfileSystem,
} from '../data/mockData';

const XLINK_NS = 'http://www.w3.org/1999/xlink';
const imageDataUrlCache = new Map();

const escapeXml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export const toAbsoluteAssetUrl = (url) => {
  if (!url || /^data:/i.test(url)) return url || '';
  if (/^https?:\/\//i.test(url)) return url;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const path = url.startsWith('/') ? url : `/${url}`;
  return `${origin}${path}`;
};

export const fetchImageAsDataUrl = async (url) => {
  const absolute = toAbsoluteAssetUrl(url);
  if (!absolute || /^data:/i.test(absolute)) return absolute;
  if (imageDataUrlCache.has(absolute)) return imageDataUrlCache.get(absolute);

  try {
    const response = await fetch(absolute);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
    imageDataUrlCache.set(absolute, dataUrl);
    return dataUrl;
  } catch {
    return absolute;
  }
};

const getSvgImageHref = (imageEl) =>
  imageEl.getAttribute('href') ||
  imageEl.getAttributeNS(XLINK_NS, 'href') ||
  '';

const setSvgImageHref = (imageEl, href) => {
  imageEl.setAttribute('href', href);
  imageEl.setAttributeNS(XLINK_NS, 'href', href);
};

export const embedSvgExternalImages = async (svgEl) => {
  if (!svgEl) return;
  const images = svgEl.querySelectorAll('image');
  await Promise.all(
    [...images].map(async (imageEl) => {
      const href = getSvgImageHref(imageEl);
      if (!href || /^data:/i.test(href)) return;
      const dataUrl = await fetchImageAsDataUrl(href);
      if (dataUrl) setSvgImageHref(imageEl, dataUrl);
    })
  );
};

/** Inline external profile images so html2canvas includes them in PDF export. */
export const prepareKeralitDrawingsForPdfExport = async (container) => {
  if (!container) return;

  const svgs = container.querySelectorAll('.vmk-drawing-inner svg');
  await Promise.all(
    [...svgs].map(async (svg) => {
      if (!svg.querySelector('image')) return;
      await embedSvgExternalImages(svg);
    })
  );

  // Drawings shown as SVG data-URL <img> (no measure SVG) — embed images into the data URL
  const imgs = container.querySelectorAll('img.vmk-drawing-img');
  await Promise.all(
    [...imgs].map(async (img) => {
      const src = img.getAttribute('src') || '';
      if (!src.startsWith('data:image/svg+xml')) return;

      let svgHtml = null;
      try {
        const comma = src.indexOf(',');
        if (comma < 0) return;
        const meta = src.slice(0, comma);
        const data = src.slice(comma + 1);
        if (/;base64/i.test(meta)) {
          const binary = atob(data);
          const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
          svgHtml = new TextDecoder('utf-8').decode(bytes);
        } else {
          svgHtml = decodeURIComponent(data);
        }
      } catch {
        return;
      }
      if (!svgHtml || !/<image[\s>]/i.test(svgHtml)) return;

      const parser = new DOMParser();
      const doc = parser.parseFromString(svgHtml, 'image/svg+xml');
      const svg = doc.querySelector('svg');
      if (!svg?.querySelector('image')) return;

      await embedSvgExternalImages(svg);
      const serialized = new XMLSerializer().serializeToString(svg);
      img.setAttribute(
        'src',
        `data:image/svg+xml;charset=utf-8,${encodeURIComponent(serialized)}`
      );
    })
  );
};

const resolveKeralitLogoHref = (profileSystemId) => {
  const logo = getKeralitProfileLogo(profileSystemId);
  const absolute = toAbsoluteAssetUrl(logo);
  return imageDataUrlCache.get(absolute) || logo;
};

export const getKeralitProfileLogo = (profileSystemId) => {
  const normalized = normalizeKeralitProfileSystem(profileSystemId);
  const profile = keralitProfileSystems.find((item) => item.id === normalized);
  return profile?.logo || keralitProfileSystems[0]?.logo || '';
};

export const getKeralitProfileName = (profileSystemId) => {
  const normalized = normalizeKeralitProfileSystem(profileSystemId);
  const profile = keralitProfileSystems.find((item) => item.id === normalized);
  return profile?.name || keralitProfileSystems[0]?.name || '190';
};

/**
 * Saved-quote / PDF thumbnail SVG for Keralit — uses the selected profile image.
 */
export const buildKeralitSavedSvg = (config) => {
  const logo = resolveKeralitLogoHref(config?.profileSystem);
  const profileName = getKeralitProfileName(config?.profileSystem);
  const safeLogo = escapeXml(logo);

  return `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 400 240" width="400" height="240" role="img" aria-label="Keralit ${escapeXml(profileName)}">
  <rect width="400" height="240" fill="#f5f5f5"/>
  <image href="${safeLogo}" xlink:href="${safeLogo}" x="40" y="20" width="320" height="200" preserveAspectRatio="xMidYMid meet"/>
</svg>`;
};

export const buildKeralitSavedSvgAsync = async (config) => {
  const logo = getKeralitProfileLogo(config?.profileSystem);
  const embeddedLogo = await fetchImageAsDataUrl(logo);
  const profileName = getKeralitProfileName(config?.profileSystem);
  const safeLogo = escapeXml(embeddedLogo);

  return `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 400 240" width="400" height="240" role="img" aria-label="Keralit ${escapeXml(profileName)}">
  <rect width="400" height="240" fill="#f5f5f5"/>
  <image href="${safeLogo}" xlink:href="${safeLogo}" x="40" y="20" width="320" height="200" preserveAspectRatio="xMidYMid meet"/>
</svg>`;
};

export const preloadKeralitProfileImages = () =>
  Promise.all(keralitProfileSystems.map((profile) => fetchImageAsDataUrl(profile.logo)));

export const getPlastisolProfileLogo = (profileSystemId) => {
  const normalized = normalizePlastisolProfileSystem(profileSystemId);
  const profile = plastisolProfileSystems.find((item) => item.id === normalized);
  return profile?.logo || plastisolProfileSystems[0]?.logo || '';
};

export const getPlastisolProfileName = (profileSystemId) => {
  const normalized = normalizePlastisolProfileSystem(profileSystemId);
  const profile = plastisolProfileSystems.find((item) => item.id === normalized);
  return profile?.name || plastisolProfileSystems[0]?.name || 'Plastisol Zetwerk';
};

const resolvePlastisolLogoHref = (profileSystemId) => {
  const logo = getPlastisolProfileLogo(profileSystemId);
  const absolute = toAbsoluteAssetUrl(logo);
  return imageDataUrlCache.get(absolute) || logo;
};

export const buildPlastisolSavedSvg = (config) => {
  const logo = resolvePlastisolLogoHref(config?.profileSystem);
  const profileName = getPlastisolProfileName(config?.profileSystem);
  const safeLogo = escapeXml(logo);

  return `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 400 240" width="400" height="240" role="img" aria-label="Plastisol ${escapeXml(profileName)}">
  <rect width="400" height="240" fill="#f5f5f5"/>
  <image href="${safeLogo}" xlink:href="${safeLogo}" x="40" y="20" width="320" height="200" preserveAspectRatio="xMidYMid meet"/>
</svg>`;
};

export const buildPlastisolSavedSvgAsync = async (config) => {
  const logo = getPlastisolProfileLogo(config?.profileSystem);
  const embeddedLogo = await fetchImageAsDataUrl(logo);
  const profileName = getPlastisolProfileName(config?.profileSystem);
  const safeLogo = escapeXml(embeddedLogo);

  return `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 400 240" width="400" height="240" role="img" aria-label="Plastisol ${escapeXml(profileName)}">
  <rect width="400" height="240" fill="#f5f5f5"/>
  <image href="${safeLogo}" xlink:href="${safeLogo}" x="40" y="20" width="320" height="200" preserveAspectRatio="xMidYMid meet"/>
</svg>`;
};

export const preloadPlastisolProfileImages = () =>
  Promise.all(plastisolProfileSystems.map((profile) => fetchImageAsDataUrl(profile.logo)));
